import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, CalendarRange, BookOpen, NotebookPen, Library, FolderTree,
  Users, GraduationCap, LogOut, ChevronLeft, ChevronRight, Menu, X, Home,
  ClipboardCheck, CheckCircle2, AlertCircle, Shield, Presentation, RotateCcw,
  Trophy, BarChart3, MessageSquare, Building2, ListTodo, Megaphone, Mail,
} from "lucide-react";
import BroadcastListener from "@/components/BroadcastListener";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import PreviewAsPicker from "@/components/PreviewAsPicker";
import WelcomePopup from "@/components/WelcomePopup";
import ConsentPrompt from "@/components/ConsentPrompt";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import { useConfirm } from "@/context/ConfirmContext";

const WEAVE_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/mur72jzi_WEAVE-LOGO%202024.webp";
const ESSEX_STARTUPS_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/9pfl78q8_Logo.jpg";

const NAV_STUDENT = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/programme", label: "Programme", icon: CalendarRange },
  { to: "/workbook", label: "Sessions", icon: BookOpen },
  { to: "/my-team", label: "My Team", icon: Users },
  { to: "/my-sponsor", label: "My Sponsor", icon: Building2 },
  { to: "/tasks", label: "Team Tasks", icon: ListTodo },
  { to: "/mentors", label: "Mentors", icon: GraduationCap },
  { to: "/mentor-booking", label: "Book My Mentor", icon: CalendarRange },
  { to: "/speakers", label: "Speakers", icon: Megaphone },
  { to: "/frameworks", label: "Framework Tools", icon: FolderTree },
  { to: "/journal", label: "Learning Journal", icon: NotebookPen },
  { to: "/resources", label: "Resource Library", icon: FolderTree },
  { to: "/library", label: "My Library", icon: Library },
  { to: "/final-pitch", label: "Final Pitch", icon: Trophy },
];

const NAV_MENTOR = [
  { to: "/mentor/dashboard", label: "My Profile", icon: GraduationCap },
  { to: "/mentor/reports", label: "My Reports", icon: MessageSquare },
  { to: "/mentor/teams-progress", label: "Teams' Progress", icon: BarChart3 },
  { to: "/mentor/team-notes", label: "Team Notes & Drop-Box", icon: NotebookPen },
  { to: "/programme", label: "Programme", icon: CalendarRange },
  { to: "/workbook", label: "Sessions", icon: BookOpen },
  { to: "/mentors", label: "All Mentors", icon: Users },
  { to: "/speakers", label: "Speakers", icon: Megaphone },
  { to: "/resources", label: "Resource Library", icon: FolderTree },
];

const NAV_ADMIN_PRIMARY = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/programme", label: "Programme", icon: CalendarRange },
  { to: "/workbook", label: "Sessions", icon: BookOpen },
  { to: "/teams", label: "Teams", icon: Users },
  { to: "/admin/sponsors", label: "Sponsors", icon: Building2 },
  { to: "/mentors", label: "Mentors", icon: GraduationCap },
  { to: "/speakers", label: "Speakers", icon: Megaphone },
  { to: "/admin/mentor-reports", label: "Mentor Reports", icon: MessageSquare },
  { to: "/resources", label: "Resource Library", icon: FolderTree },
  { to: "/frameworks", label: "Framework Tools", icon: FolderTree },
  { to: "/retrospective", label: "Retrospective", icon: BarChart3 },
];

const NAV_ADMIN_TOOLS = [
  { to: "/roster", label: "Student Roster", icon: ClipboardCheck },
  { to: "/attendance", label: "Attendance & Food", label_short: "Attendance", icon: CheckCircle2 },
  { to: "/admin/final-pitches", label: "Final Pitches", icon: Trophy },
  { to: "/admin/team-questions", label: "Team Questions", icon: MessageSquare },
  { to: "/admin/surveys", label: "Survey Completion", icon: ClipboardCheck },
  { to: "/admin/email-log", label: "Email Log", icon: Mail },
  { to: "/admin/email-templates", label: "Email Templates", icon: Mail },
  { to: "/admin/uploader", label: "Bulk Uploader", icon: Library },
  { to: "/admin/admins", label: "Manage Admins", icon: Shield },
  { to: "/admin/preview-platform", label: "Programme Settings", icon: Presentation },
  { to: "/admin/demo-reset", label: "Data Reset", icon: RotateCcw },
];

export default function Layout() {
  const confirm = useConfirm();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [programme, setProgramme] = useState({});
  const [myTeam, setMyTeam] = useState(null);
  const [resourceGaps, setResourceGaps] = useState(0);

  useEffect(() => {
    if (!user || user.role !== "mentor") { setResourceGaps(0); return; }
    api.get("/mentor/resource-gaps").then(({ data }) => setResourceGaps(data?.count || 0)).catch(() => setResourceGaps(0));
  }, [user?.id, user?.role, location.pathname]);

  useEffect(() => {
    api.get("/programme").then(({ data }) => setProgramme(data || {})).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    if (!user || user.role !== "student") { setMyTeam(null); return; }
    let cancelled = false;
    api.get("/teams/my")
      .then(({ data }) => { if (!cancelled) setMyTeam(data?.team || null); })
      .catch(() => { if (!cancelled) setMyTeam(null); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role, location.pathname]);

  const isAdmin = user && (user.role === "admin" || user.role === "faculty");
  const isMentor = user && user.role === "mentor";
  const isStudent = user && user.role === "student";
  const navPrimary = isAdmin ? NAV_ADMIN_PRIMARY : (isMentor ? NAV_MENTOR : NAV_STUDENT);

  const openingDone = !!user?.opening_survey_completed;
  const exitDone = !!user?.exit_survey_completed;
  const hasOpening = !!programme.opening_survey_url;
  const hasExit = !!programme.exit_survey_url;

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const handleHome = async () => {
    if ((await confirm({ title: "Sign out?", description: "You'll be returned to the sign-in page.", destructive: true }))) {
      await logout(); navigate("/login");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FDFDFD]">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#E5E7EB] flex items-center justify-between px-4 h-14">
        <button onClick={() => setMobileOpen((s) => !s)} data-testid="mobile-menu-toggle" className="p-2">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <div className="flex items-center gap-2">
          <img src={ESSEX_STARTUPS_LOGO} alt="" className="h-7 w-7 object-cover" />
          <span className="font-display font-bold tracking-tight text-[#0F1F2E]">iTeams</span>
        </div>
        <div className="w-8" />
      </div>

      <aside
        className={`${collapsed ? "md:w-[68px]" : "md:w-[260px]"} ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 left-0 z-30 h-screen flex-shrink-0 bg-white border-r border-[#E5E7EB] flex flex-col transition-all duration-200`}
        data-testid="sidebar">
        <button onClick={handleHome} data-testid="sidebar-home-btn"
          className="px-4 py-5 border-b border-[#E5E7EB] flex items-center gap-3 text-left hover:bg-[#F3F4F6] transition-colors w-full group">
          <img src={ESSEX_STARTUPS_LOGO} alt="Essex Startups" className="h-10 w-10 object-cover flex-shrink-0" />
          {!collapsed && (
            <div className="leading-tight flex-1 min-w-0">
              <div className="font-display font-black tracking-tight text-[#0F1F2E]">iTeams</div>
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">
                {programme.term_label || "University of Essex"}
              </div>
              {user && (
                <div className="mt-1.5" data-testid="sidebar-context-badge">
                  {isAdmin && (
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] bg-[#0F1F2E] text-white px-1.5 py-0.5 font-semibold">
                      <Shield size={9} /> Admin
                    </span>
                  )}
                  {isMentor && (
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] bg-[#25AAAA] text-white px-1.5 py-0.5 font-semibold">
                      <GraduationCap size={9} /> Mentor
                    </span>
                  )}
                  {isStudent && (
                    myTeam
                      ? <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] bg-[#0F1F2E] text-white px-1.5 py-0.5 font-semibold max-w-full truncate">
                          <Users size={9} /> <span className="truncate">{myTeam.name}</span>
                        </span>
                      : <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] bg-[#FEF3D0] border border-[#f4b847] text-[#7A5B17] px-1.5 py-0.5 font-semibold">
                          <AlertCircle size={9} /> Join a team
                        </span>
                  )}
                </div>
              )}
            </div>
          )}
          {!collapsed && <Home size={14} className="text-[#525252] opacity-0 group-hover:opacity-100 transition-opacity" />}
        </button>

        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
          <div>
            {!collapsed && <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] px-3 mb-2">Programme</div>}
            <ul className="space-y-1">
              {navPrimary.map((n) => (
                <NavItem key={n.to} item={n} collapsed={collapsed} onClick={() => setMobileOpen(false)}
                  count={n.to === "/mentor/team-notes" && resourceGaps > 0 ? resourceGaps : null} />
              ))}
            </ul>
          </div>

          {isAdmin && (
            <div data-testid="nav-admin-tools">
              {!collapsed && <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] px-3 mb-2">Admin tools</div>}
              <ul className="space-y-1">
                {NAV_ADMIN_TOOLS.map((n) => (
                  <NavItem key={n.to} item={n} collapsed={collapsed} onClick={() => setMobileOpen(false)} />
                ))}
              </ul>
            </div>
          )}

          {!isMentor && (hasOpening || hasExit || isAdmin) && (
            <div>
              {!collapsed && <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] px-3 mb-2">Surveys</div>}
              <ul className="space-y-1">
                <NavItem item={{ to: "/survey/opening", label: "Entry Survey", icon: ClipboardCheck }}
                  collapsed={collapsed} onClick={() => setMobileOpen(false)}
                  badge={openingDone
                    ? { icon: CheckCircle2, color: "text-[#25AAAA]", title: "Completed" }
                    : hasOpening ? { icon: AlertCircle, color: "text-[#f4b847]", title: "Please complete" } : null}/>
                <NavItem item={{ to: "/survey/exit", label: "Exit Survey", icon: ClipboardCheck }}
                  collapsed={collapsed} onClick={() => setMobileOpen(false)}
                  badge={exitDone
                    ? { icon: CheckCircle2, color: "text-[#25AAAA]", title: "Completed" }
                    : hasExit ? { icon: AlertCircle, color: "text-[#f4b847]", title: "Please complete" } : null}/>
              </ul>
            </div>
          )}
        </nav>

        <div className="border-t border-[#E5E7EB] p-3 space-y-3">
          {user && (
            <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 bg-[#25AAAA] text-white flex items-center justify-center text-xs font-semibold uppercase flex-shrink-0">
                {(user.name || user.email || "U").slice(0, 2)}
              </div>
              {!collapsed && (
                <div className="leading-tight min-w-0">
                  <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#525252]">{user.role}</div>
                </div>
              )}
            </div>
          )}
          {isAdmin && !localStorage.getItem("weave_token_admin_backup") && (
            <div className="px-2"><PreviewAsPicker collapsed={collapsed} /></div>
          )}
          <div className="flex items-center justify-between gap-2 px-2">
            <button onClick={handleLogout} data-testid="logout-btn"
              className="flex-1 flex items-center justify-center gap-2 border border-[#0F1F2E] text-[#0F1F2E] text-xs font-semibold uppercase tracking-[0.16em] px-3 py-2 hover:bg-[#25AAAA] hover:text-white hover:border-[#25AAAA] transition-colors">
              <LogOut size={14} />
              {!collapsed && <span>Sign out</span>}
            </button>
            <button onClick={() => setCollapsed((s) => !s)} data-testid="sidebar-collapse-btn"
              className="hidden md:inline-flex items-center justify-center border border-[#E5E7EB] p-2 hover:border-[#0F1F2E]" aria-label="Collapse sidebar">
              {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 mt-14 md:mt-0">
        <ImpersonationBanner user={user} />
        <WelcomePopup />
        <ConsentPrompt />
        <ForcePasswordChange />
        <header className="hidden md:flex items-center justify-between px-8 py-5 border-b border-[#E5E7EB] bg-white sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <span className="inline-block w-2.5 h-2.5 bg-[#F4B847]" />
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-[#0F1F2E]" data-testid="page-title">
                {titleFromPath(location.pathname)}
              </h1>
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mt-0.5">
                iTeams · {programme.term_label || "University of Essex"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6 opacity-90">
            <img src={WEAVE_LOGO} alt="The Weave" className="h-10 w-auto object-contain" />
            <img src={ESSEX_STARTUPS_LOGO} alt="Essex Startups" className="h-10 w-10 object-cover" />
          </div>
        </header>

        <main className="flex-1 px-4 md:px-10 py-6 md:py-10">
          <Outlet />
        </main>

        <BroadcastListener />

        <footer className="border-t border-[#E5E7EB] bg-white px-8 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="text-xs text-[#525252]">
            iTeams · University of Essex · Delivered with The Weave
          </div>
          <div className="flex items-center gap-6">
            <img src={WEAVE_LOGO} alt="" className="h-7 w-auto" />
            <img src={ESSEX_STARTUPS_LOGO} alt="" className="h-7 w-7 object-cover" />
          </div>
        </footer>
      </div>
    </div>
  );
}

function NavItem({ item, collapsed, onClick, badge, count }) {
  const Icon = item.icon;
  const BadgeIcon = badge?.icon;
  return (
    <li>
      <NavLink to={item.to} end={item.to === "/dashboard"} onClick={onClick}
        data-testid={`nav-${item.to.replace(/^\//, "").replace(/\//g, "-") || "home"}`}
        title={badge?.title}
        className={({ isActive }) =>
          `group relative flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
            isActive ? "border-[#25AAAA] bg-[#F3F4F6] text-[#0F1F2E] font-medium"
                     : "border-transparent text-[#525252] hover:text-[#0F1F2E] hover:bg-[#F3F4F6]"
          }`}>
        <Icon size={16} strokeWidth={1.5} />
        {!collapsed && <span className="truncate flex-1">{item.label}</span>}
        {count != null && (
          <span title={`${count} team${count === 1 ? "" : "s"} with no sponsor resources yet`}
            className={`shrink-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#e93e36] text-white text-[10px] font-bold ${collapsed ? "absolute -top-0.5 -right-0.5" : ""}`}
            data-testid={`nav-count-${item.to.replace(/^\//, "").replace(/\//g, "-")}`}>
            {count}
          </span>
        )}
        {BadgeIcon && !collapsed && (
          <BadgeIcon size={14} className={`shrink-0 ${badge.color}`} strokeWidth={1.8} />
        )}
      </NavLink>
    </li>
  );
}

function titleFromPath(path) {
  const map = {
    "/dashboard": "Dashboard",
    "/programme": "Programme",
    "/workbook": "Sessions",
    "/my-team": "My Team",
    "/my-sponsor": "My Sponsor",
    "/tasks": "Team Tasks",
    "/mentors": "Mentors",
    "/mentor/dashboard": "Mentor Dashboard",
    "/mentor/reports": "My Mentor Reports",
    "/mentor/team-notes": "My Teams' Notes",
    "/mentor-booking": "Book My Mentor",
    "/mentor/teams-progress": "Teams' Progress",
    "/speakers": "Speakers",
    "/frameworks": "Framework Tools",
    "/journal": "Learning Journal",
    "/resources": "Resource Library",
    "/library": "My Library",
    "/final-pitch": "Final Pitch",
    "/teams": "Teams",
    "/admin/sponsors": "Sponsors",
    "/admin/mentor-reports": "Mentor Reports",
    "/admin/final-pitches": "Final Pitches",
    "/admin/team-questions": "Team Questions",
    "/admin/surveys": "Survey Completion",
    "/admin/email-log": "Email Log",
    "/admin/email-templates": "Email Templates",
    "/admin/uploader": "Bulk Uploader",
    "/admin/admins": "Manage Admins",
    "/admin/preview-platform": "Programme Settings",
    "/admin/demo-reset": "Data Reset",
    "/roster": "Student Roster",
    "/retrospective": "Retrospective",
    "/survey": "Survey",
  };
  const base = "/" + (path.split("/")[1] || "");
  const two = "/" + (path.split("/")[1] || "") + "/" + (path.split("/")[2] || "");
  return map[path] || map[two] || map[base] || "iTeams";
}
